/**
 * @lines98/client/services/daily-cache — IndexedDB persistence для daily-board attempts.
 *
 * **Boundary (load-bearing).** This cache is **UX-only**. Source-of-truth для
 * «одна попытка/день» — сервер (daily_attempts atomic PK per-player/per-date,
 * см. `packages/server/src/routes/finish-game.ts` + `packages/server/src/db/schema.ts`).
 * Per dispatch_t-7 §3: «НЕ полагайся на client cache как source-of-truth.
 * Если cache говорит "не играл", но сервер возвращает duplicate_attempt — server wins.»
 *
 * **PER-PLAYER ключи (owner-решение зад.8, тикет fe-playtest-round2 §F).** Ключи
 * `played`/`in_progress` суффиксованы `playerId` (`getCurrent()?.playerId`, тем же
 * приёмом, что `lib/screen-snapshot` + `lib/daily-played-snapshot`) → другой аккаунт
 * на том же устройстве НЕ блокируется чужой сыгранной доской. Гость без identity →
 * нет ключа → пусто → fail-open (старт разрешён, сервер backstop). Смена ключа на
 * per-player НЕ снимает границу: кэш остаётся UX-проекцией, повторную ЗАЧЁТНУЮ
 * попытку открыть нельзя — сервер всё равно отклонит (анти-чит цел, AC-F.4/F.7).
 *
 * Purpose:
 *   1. **In-progress recovery (AC-5):** survive refresh; restore live attempt
 *      (seed + moves[]) so player не теряет прогресс. Per `core-daily-board.md` §4.3.
 *   2. **Done-state UX (AC-2):** show «Сегодня уже сыграно» on daily screen без
 *      pre-querying server. Server still authoritative — see boundary above.
 *
 * Storage shape (single IndexedDB store named `lines98.daily.v1`):
 *   - Key `'in_progress'`: in-progress record (deleted on submit or stale rollover).
 *   - Key `'played'`: played-today record (set after successful or duplicate_attempt
 *     submit response).
 *
 * Stale handling:
 *   - On read, если record.dateOfDay !== currentDateOfDay, treat as absent and
 *     delete (rollover-handling is T-9 — full auto-finish belongs there; for T-7
 *     we just discard stale state per dispatch §2 C4 «Clear после submit OR при
 *     dateOfDay !== current»).
 *
 * Storage backend:
 *   - Primary: IndexedDB через `idb-keyval` (already а dep — see services/identity.ts).
 *   - Fallback: localStorage (когда IndexedDB unavailable, e.g. private browsing).
 *   - All ops async + defensive (catch + ignore) — cache failures degrade UX
 *     gracefully, never block server submit.
 *
 * Cross-references:
 *   - dispatch_t-7_game-developer.md §2 C4 + §3 (server enforcement).
 *   - core-daily-board.md §4.3 (local cache shape) + §2.2 (one attempt).
 *   - services/identity.ts (sibling pattern — same IDB-with-localStorage-fallback).
 */

import { get as idbGet, set as idbSet, del as idbDel } from 'idb-keyval';

import type { CellValue, FinishReason, Move, NextBall } from '@lines98/shared/types/game';

import {
  clearIdleMarker,
  clearInProgressMarker,
  clearPlayedSnapshot,
  saveInProgressMarker,
  savePlayedSnapshot,
  type InProgressBoardView,
} from '../lib/daily-played-snapshot.js';
import { getCurrent } from './identity.js';

// =============================================================================
// Types
// =============================================================================

/**
 * In-progress daily attempt record. Saved after каждый valid `applyMove`.
 *
 * Schema version `v: 1` — bump on shape change (record with mismatched v is
 * treated как corrupted and deleted on next read).
 */
export interface InProgressRecord {
  readonly v: 1;
  /** Calendar `date_of_day` (YYYY-MM-DD per `DAILY_ROLLOVER_BOUNDARY_UTC_HOURS`). */
  readonly dateOfDay: string;
  /** uint32 seed from `GET /daily/seed` (server-authoritative). */
  readonly seed: number;
  /** Recorded valid moves (anti-cheat replayScore feeds на this). */
  readonly moves: Move[];
  /** ms epoch — для potential «brought from yesterday» detection (T-9 territory). */
  readonly timestamp: number;
}

/**
 * Immutable snapshot of the player's FINAL board, captured at finish-time (the
 * store still holds the final `engineState`). Powers the daily done-state
 * (core-daily-board.md §2.3-DONE): the final board is rendered from this snapshot
 * instantly, with no network — and it survives a refresh. The snapshot is
 * deterministically derivable from `seed + moves[]`, so on a cache miss (fresh
 * device) `lib/daily-final-state` re-simulates it instead; this field just lets
 * the same-device path skip that work.
 *
 * Optional on `PlayedRecord` — the `duplicate_attempt` stub (server already had
 * an attempt; we never replayed it locally) has no snapshot, and old records
 * predate this field. Absence → done-state falls back to re-simulation / the
 * simpler card.
 */
export interface FinalBoardSnapshot {
  /** Final 9×9 grid (row-major), byte-equal to the in-game board at finish. */
  readonly grid: CellValue[][];
  /** The 3 next-balls (niche) at finish — shown as a snapshot (owner 2026-06-09). */
  readonly next: NextBall[];
  /** Total moves played (board plashka «Ходы»). */
  readonly movesCount: number;
  /** How the attempt ended (board_full / player_finish / rollover). */
  readonly finishReason: FinishReason;
}

/**
 * Played-today record. Saved after server `/finish-game` returns success OR
 * `duplicate_attempt` (server is canonical, but cache mirrors so UI doesn't
 * re-query за each daily-screen mount).
 */
export interface PlayedRecord {
  readonly v: 1;
  readonly dateOfDay: string;
  /** `score_verified` from server response (NOT score_claimed — anti-cheat.md §3.2). */
  readonly scoreVerified: number;
  /** Server replay id (UUIDv7-base — ADR-0003). */
  readonly replayId: string | null;
  /** Optional — present only on `verified` path, absent on `duplicate_attempt`. */
  readonly linesVerified?: number;
  /**
   * Replay URL key (`short_id`, ADR-0003) of the player's own daily replay —
   * the key the `/api/replay/<short_id>` endpoint uses. Lets the done-state link
   * to / re-fetch the own replay without a leaderboard round-trip. Optional:
   * absent on the `duplicate_attempt` stub and on records predating this field
   * (core-daily-board.md §2.3-DONE).
   */
  readonly shortId?: string | null;
  /**
   * Immutable final-board snapshot (see {@link FinalBoardSnapshot}). Present on
   * the `verified` path (we held the final state); absent otherwise → re-sim.
   */
  readonly finalBoard?: FinalBoardSnapshot;
  /**
   * LAST-KNOWN daily rank of the player (their `me`-row position), cached so the
   * done-state can paint the rank plate INSTANTLY on the next mount instead of
   * leaving it blank until `fetchOwnDailyStats` resolves async (owner-playtest fix
   * 2026-06-11 «ранг мелькает на сыгранной доске»). The rank is LIVE for the day —
   * it can shift as others play — so it's only a fast first paint: the done-state
   * still reconciles to server-truth (`hydrateDone`) and re-writes this field. Absent
   * on records predating this field / before the first leaderboard fetch.
   */
  readonly lastRank?: number;
  readonly timestamp: number;
}

// =============================================================================
// Constants
// =============================================================================

/**
 * Ключи кэша — PER-PLAYER (owner-решение зад.8, тикет fe-playtest-round2 §F).
 *
 * ЗАЧЕМ: прежде ключи были ГЛОБАЛЬНЫМИ на устройство → другой аккаунт на том же
 *   устройстве блокировался чужой сыгранной доской («сыграно» аккаунта A читалось
 *   аккаунтом B). Теперь полный ключ = `${PREFIX}.${playerId}` — изоляция по игроку,
 *   ТЕМ ЖЕ приёмом, что `lib/screen-snapshot.ts:100` (`getCurrent()?.playerId`).
 *   Синхронные снимки (`lib/daily-played-snapshot`) уже per-player через тот же
 *   источник identity → канон IDB и зеркало-снимок не разъезжаются (AC-F.6).
 *
 * Гость без identity: `currentKey()` → null → read = null, write/delete = no-op
 *   (fail-open: нет ключа → старт разрешён; сервер backstop-ит на сабмите, AC-F.5).
 *   Граница не меняется: кэш остаётся UX-проекцией, server-truth — `daily_attempts`
 *   (см. module header «Boundary»; AC-F.4/F.7 — анти-чит цел).
 */
const IN_PROGRESS_KEY_PREFIX = 'lines98.daily.in_progress.v1';
const PLAYED_KEY_PREFIX = 'lines98.daily.played.v1';

/**
 * Полный per-player ключ из префикса, либо null для гостя (нет identity → нет
 * ключа → fail-open). Тот же источник playerId, что `lib/screen-snapshot` и
 * `lib/daily-played-snapshot` — все daily-проекции одного игрока согласованы.
 */
function currentKey(prefix: string): string | null {
  const id = getCurrent()?.playerId;
  return id ? `${prefix}.${id}` : null;
}

// =============================================================================
// IndexedDB / localStorage helpers (mirrors identity.ts sibling pattern)
// =============================================================================

async function readKey<T>(key: string, guard: (v: unknown) => v is T): Promise<T | null> {
  try {
    const raw = (await idbGet(key)) as unknown;
    if (guard(raw)) return raw;
    return null;
  } catch {
    try {
      const fallback = typeof window !== 'undefined' ? window.localStorage?.getItem(key) : null;
      if (!fallback) return null;
      const parsed = JSON.parse(fallback) as unknown;
      return guard(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
}

async function writeKey(key: string, value: unknown): Promise<void> {
  try {
    await idbSet(key, value);
  } catch {
    try {
      if (typeof window !== 'undefined') {
        window.localStorage?.setItem(key, JSON.stringify(value));
      }
    } catch {
      // both failed — non-fatal; in-memory state continues correctly.
    }
  }
}

async function deleteKey(key: string): Promise<void> {
  try {
    await idbDel(key);
  } catch {
    // ignore
  }
  try {
    if (typeof window !== 'undefined') {
      window.localStorage?.removeItem(key);
    }
  } catch {
    // ignore
  }
}

// =============================================================================
// Type guards
// =============================================================================

function isMove(value: unknown): value is Move {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v['sr'] === 'number' &&
    typeof v['sc'] === 'number' &&
    typeof v['r'] === 'number' &&
    typeof v['c'] === 'number'
  );
}

function isInProgressRecord(value: unknown): value is InProgressRecord {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    v['v'] === 1 &&
    typeof v['dateOfDay'] === 'string' &&
    typeof v['seed'] === 'number' &&
    Array.isArray(v['moves']) &&
    (v['moves'] as unknown[]).every(isMove) &&
    typeof v['timestamp'] === 'number'
  );
}

function isFinalBoardSnapshot(value: unknown): value is FinalBoardSnapshot {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    Array.isArray(v['grid']) &&
    (v['grid'] as unknown[]).every(
      (row) => Array.isArray(row) && (row as unknown[]).every((c) => typeof c === 'number'),
    ) &&
    Array.isArray(v['next']) &&
    typeof v['movesCount'] === 'number' &&
    typeof v['finishReason'] === 'string'
  );
}

function isPlayedRecord(value: unknown): value is PlayedRecord {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  // Optional fields: validated only when present (strict-if-present). A malformed
  // optional field means a corrupted record → reject (re-sim rebuilds it).
  if ('shortId' in v && v['shortId'] !== null && typeof v['shortId'] !== 'string') {
    return false;
  }
  if ('finalBoard' in v && v['finalBoard'] !== undefined && !isFinalBoardSnapshot(v['finalBoard'])) {
    return false;
  }
  if ('lastRank' in v && v['lastRank'] !== undefined && typeof v['lastRank'] !== 'number') {
    return false;
  }
  return (
    v['v'] === 1 &&
    typeof v['dateOfDay'] === 'string' &&
    typeof v['scoreVerified'] === 'number' &&
    (v['replayId'] === null || typeof v['replayId'] === 'string') &&
    typeof v['timestamp'] === 'number'
  );
}

// =============================================================================
// Public API — in-progress
// =============================================================================

/**
 * Save the current in-progress daily attempt. Overwrites any prior in-progress
 * record. Call after каждый valid `applyMove` (or batch с RAF в hot loops).
 *
 * @param boardView — ОПЦИОНАЛЬНЫЙ визуальный снимок доски (grid+next+score+movesCount),
 *   снятый из живого состояния движка. Кладётся в СИНХРОННЫЙ in-progress-маркер →
 *   при возврате к незавершённой партии доска дня рисуется МГНОВЕННО из снимка, без кадра
 *   «загрузка» (owner-bug A 2026-06-28). НЕ персистится в IDB-канон (там seed+moves — этого
 *   достаточно для движкового восстановления; снимок — лишь UX-проекция показа).
 */
export async function saveInProgress(
  record: Omit<InProgressRecord, 'v'>,
  boardView?: InProgressBoardView,
): Promise<void> {
  const persisted: InProgressRecord = {
    v: 1,
    dateOfDay: record.dateOfDay,
    seed: record.seed >>> 0,
    moves: record.moves.slice(),
    timestamp: record.timestamp,
  };
  // Зеркалим факт «партия НАЧАТА» (+ визуальный снимок доски) в СИНХРОННЫЙ in-progress-
  // маркер (localStorage) ДО async IDB-записи — чтобы при следующем mount доски дня экран
  // СИНХРОННО знал, что надо вести в resume, и МГНОВЕННО нарисовал доску из снимка, а НЕ
  // рисовал стартовую/«загрузку» (owner-bug 2026-06-13 + A 2026-06-28, instant-ui-hydration
  // §1). Один write-path (этот) → IDB-канон `in_progress` и маркер не дрейфуют.
  saveInProgressMarker(record.dateOfDay, boardView);
  // Партия начата → idle-маркер «не сыграно» (если был с захода ДО старта) больше не
  // корректен: иначе синхронный mount нарисует idle поверх начатой партии (и был флип).
  // idle и in-progress — взаимоисключающие проекции дня; снимаем тем же write-path'ом.
  clearIdleMarker();
  // Per-player ключ; гость (нет identity) → null → no-op (запись не «утекает» в общий
  // ключ, видимый другому аккаунту; fail-open — сервер backstop на сабмите). AC-F.5.
  const key = currentKey(IN_PROGRESS_KEY_PREFIX);
  if (key) await writeKey(key, persisted);
}

/**
 * Load the in-progress daily attempt, OR null if absent OR stale (different
 * dateOfDay → discarded). Stale-detected records are also deleted as а side
 * effect (cleanup без waiting for next save).
 *
 * @param currentDateOfDay — today's `date_of_day` from `GET /daily/seed`.
 *   Passed in explicitly to keep this fn pure-async (no global clock reach).
 */
export async function loadInProgress(currentDateOfDay: string): Promise<InProgressRecord | null> {
  // Per-player ключ; гость → null → null (fail-open: нет ключа → нет начатой партии). AC-F.5.
  const key = currentKey(IN_PROGRESS_KEY_PREFIX);
  if (!key) return null;
  const record = await readKey(key, isInProgressRecord);
  if (!record) return null;
  if (record.dateOfDay !== currentDateOfDay) {
    // Stale (rollover happened) — discard. Full T-9 work (auto-finish + submit
    // of yesterday's partial) is out of T-7 scope per dispatch §0. Снимаем и
    // синхронный in-progress-маркер (вчерашняя начатая партия не блокирует idle нового
    // дня — loadInProgressMarker сам date-инвалидируется, но один write-path не залипает).
    clearInProgressMarker();
    await deleteKey(key);
    return null;
  }
  return record;
}

/**
 * Load а **brought-from-yesterday** in-progress record — one whose `dateOfDay`
 * differs from today's (rollover happened while the tab was closed). Unlike
 * `loadInProgress`, this does **NOT** delete the record: the caller must first
 * re-simulate + submit it as а `finish_reason='rollover'` game for THAT past day
 * (core-daily-board.md §7.3 + AC-12), then call `clearInProgress()` explicitly.
 *
 * Returns `null` when there's no in-progress record, OR the record is for today
 * (that's the live-recovery case — use `loadInProgress`).
 *
 * @param currentDateOfDay — today's `date_of_day` from `GET /daily/seed`.
 */
export async function loadBroughtFromYesterday(
  currentDateOfDay: string,
): Promise<InProgressRecord | null> {
  // Per-player ключ; гость → null → null (нет brought-from-yesterday для гостя). AC-F.5.
  const key = currentKey(IN_PROGRESS_KEY_PREFIX);
  if (!key) return null;
  const record = await readKey(key, isInProgressRecord);
  if (!record) return null;
  // Today's record → not a brought-from-yesterday case.
  if (record.dateOfDay === currentDateOfDay) return null;
  return record;
}

/** Clear the in-progress record (call after submit success OR explicit reset). */
export async function clearInProgress(): Promise<void> {
  // Партия завершена/сброшена → синхронный in-progress-маркер больше не корректен
  // (иначе следующий mount уйдёт в resume несуществующей партии). Один write-path.
  clearInProgressMarker();
  // Per-player ключ; гость → no-op (нечего чистить).
  const key = currentKey(IN_PROGRESS_KEY_PREFIX);
  if (key) await deleteKey(key);
}

// =============================================================================
// Public API — played-today
// =============================================================================

/**
 * Save the played-today marker. Call after server returns either `verified` or
 * `duplicate_attempt` (server canonical — see boundary in module header).
 */
export async function savePlayed(record: Omit<PlayedRecord, 'v'>): Promise<void> {
  const persisted: PlayedRecord = {
    v: 1,
    dateOfDay: record.dateOfDay,
    scoreVerified: record.scoreVerified,
    replayId: record.replayId,
    ...(record.linesVerified !== undefined ? { linesVerified: record.linesVerified } : {}),
    ...(record.shortId !== undefined ? { shortId: record.shortId } : {}),
    ...(record.finalBoard !== undefined ? { finalBoard: record.finalBoard } : {}),
    ...(record.lastRank !== undefined ? { lastRank: record.lastRank } : {}),
    timestamp: record.timestamp,
  };
  // Зеркалим played-факт в СИНХРОННЫЙ snapshot (localStorage) ДО async IDB-записи,
  // чтобы факт «сыграно» был доступен в тот же тик при следующем mount доски дня —
  // экран рисует done СРАЗУ, без промежуточного idle-кадра (instant-ui-hydration §1).
  // Один write-path (этот) → IDB-канон и снимок не дрейфуют. Defensive — ствол глотает
  // сбой записи, server-truth всё равно истина.
  savePlayedSnapshot(persisted);
  // Игрок сыграл → idle-маркер «не сыграно» И in-progress-маркер «партия начата» (если
  // были) больше не корректны: done — терминальное состояние дня, оно вытесняет оба
  // (иначе следующий mount нарисует idle / уйдёт в resume поверх сыгранной доски). Все
  // три проекции дня взаимоисключающие; снимаем лишние тем же синхронным write-path'ом.
  clearIdleMarker();
  clearInProgressMarker();
  // Per-player ключ; гость → null → no-op (гостевая запись НЕ становится «сыгранной
  // доской» залогинившегося аккаунта; fail-open — сервер backstop). AC-F.5.
  const key = currentKey(PLAYED_KEY_PREFIX);
  if (key) await writeKey(key, persisted);
}

/**
 * Load played-today record, OR null if absent/stale. Stale records (different
 * dateOfDay) are deleted as а side effect.
 *
 * **Backfill of the synchronous mirror (owner-playtest fix 2026-06-10, second pass).**
 * The played-snapshot mirror is only written by `savePlayed` — so an attempt finished
 * BEFORE the mirror existed (pre-#8), or recorded on another device, leaves the IDB
 * canon present but the localStorage snapshot empty. The next daily-screen mount then
 * couldn't decide synchronously and (old behaviour) flashed an IDLE invite before this
 * async read flipped it to DONE. Here we BACKFILL the snapshot from the freshly-loaded
 * IDB canon so the VERY NEXT mount is instant — same write-path as `savePlayed`, no
 * drift. No-op for a guest (mirror is a no-op without identity).
 */
export async function loadPlayed(currentDateOfDay: string): Promise<PlayedRecord | null> {
  // Per-player ключ; гость → null → null (fail-open: нет ключа → «не сыграно» →
  // старт разрешён; сервер backstop на сабмите). Чужой device-кэш не виден. AC-F.2/F.5.
  const key = currentKey(PLAYED_KEY_PREFIX);
  if (!key) return null;
  const record = await readKey(key, isPlayedRecord);
  if (!record) return null;
  if (record.dateOfDay !== currentDateOfDay) {
    // Rollover — вчерашний снимок; чистим обе проекции (IDB-канон + синхронный mirror),
    // чтобы done-state прошлого дня не показался на новой доске. idle-маркер прошлого
    // дня тоже снимаем (loadIdleMarker сам date-инвалидируется, но один write-path —
    // не залипает; чистим обе daily-проекции вместе).
    clearPlayedSnapshot();
    clearIdleMarker();
    clearInProgressMarker();
    await deleteKey(key);
    return null;
  }
  // Backfill: mirror has the same shape as the canon → write it back so subsequent
  // mounts read «сыграно» synchronously (idempotent: re-writing an equal snapshot is
  // harmless). Lecit pre-#8 games + cross-device after the first visit.
  savePlayedSnapshot(record);
  return record;
}

/** Clear played-today record (e.g., explicit cache reset для tests). */
export async function clearPlayed(): Promise<void> {
  // Зеркало played-снимка + idle-маркер + in-progress-маркер чистим вместе с IDB-каноном
  // (один путь — не залипает; played сбросили → ни idle, ни «начато» уже не актуальны).
  clearPlayedSnapshot();
  clearIdleMarker();
  clearInProgressMarker();
  // Per-player ключ; гость → no-op. Чистит ТОЛЬКО запись текущего игрока — чужую
  // played-запись другого аккаунта на устройстве не затрагивает (AC-F.4).
  const key = currentKey(PLAYED_KEY_PREFIX);
  if (key) await deleteKey(key);
}

// =============================================================================
// Test seam — clear all daily cache state (used by qa-tester / integration tests)
// =============================================================================

/**
 * Reset both in-progress + played records for the CURRENT player. Tests only —
 * production never calls. Per-player keys (§F): чистит записи текущей identity
 * (тесты переключают identity между кейсами + сами wipe-ают in-memory IDB-store).
 */
export async function _clearAllForTests(): Promise<void> {
  clearPlayedSnapshot();
  clearIdleMarker();
  clearInProgressMarker();
  const ip = currentKey(IN_PROGRESS_KEY_PREFIX);
  if (ip) await deleteKey(ip);
  const pl = currentKey(PLAYED_KEY_PREFIX);
  if (pl) await deleteKey(pl);
}
